import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { PathwayBadge } from "./DonorParts";
import {
  calculateEngagement,
  resolveEffectivePathway,
  getMemberActivities,
} from "../../utils/engagementScoring";

// ── Column Definitions ──────────────────────────────────────────
const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "organization", label: "Organization" },
  { key: "email", label: "Email" },
  { key: "type", label: "Type" },
  { key: "pathway", label: "Pathway" },
  { key: "flags", label: "Flags", sortable: false },
  { key: "director", label: "Director" },
];

// ── Sort helpers ────────────────────────────────────────────────
const getDisplayName = (m) =>
  m.donorType === "organization"
    ? m.organization || "Organization"
    : m.donorType === "household"
      ? m.householdName || `${m.firstName || ""} ${m.lastName || ""}`
      : `${m.lastName || ""}, ${m.firstName || ""}`;

const getOrgDisplay = (m) =>
  m.donorType === "organization"
    ? [m.firstName, m.lastName].filter(Boolean).join(" ") || ""
    : m.organization || "";

const getTypeLabel = (m) =>
  m.donorType === "household" ? "HH" : m.donorType === "organization" ? "Org" : "";

const getSortValue = (member, key, effectiveMap) => {
  switch (key) {
    case "name": return getDisplayName(member).toLowerCase();
    case "organization": return getOrgDisplay(member).toLowerCase();
    case "email": return (member.email || "").toLowerCase();
    case "type": return getTypeLabel(member);
    case "pathway": return effectiveMap[member.id]?.label || "";
    case "director": return (member.assignedDirector || "").toLowerCase();
    default: return "";
  }
};

// ═══════════════════════════════════════════════════════════════

export const DonorListTable = ({ members }) => {
  const { palette, navigate, pathways, allGifts, allEventAttendance, allSponsorships } = useApp();
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  // Pre-compute effective pathway for all members in the table
  const effectiveMap = useMemo(() => {
    const map = {};
    members.forEach((m) => {
      const activities = getMemberActivities(m.id, allGifts, allEventAttendance, allSponsorships);
      const engagement = calculateEngagement(m, undefined, activities);
      const lifetimeGiving = (activities.memberGifts || []).reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
      map[m.id] = resolveEffectivePathway(m, engagement.score, pathways, undefined, { lifetimeGiving });
    });
    return map;
  }, [members, pathways, allGifts, allEventAttendance, allSponsorships]);

  // Sort members
  const sorted = useMemo(() => {
    return [...members].sort((a, b) => {
      const va = getSortValue(a, sortCol, effectiveMap);
      const vb = getSortValue(b, sortCol, effectiveMap);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [members, sortCol, sortDir, effectiveMap]);

  const handleSort = (key) => {
    const col = COLUMNS.find((c) => c.key === key);
    if (col?.sortable === false) return;
    if (sortCol === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => {
    if (col.sortable === false) return null;
    const isActive = sortCol === col.key;
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4, verticalAlign: "middle", lineHeight: 0 }}>
        <ChevronUp size={ICON_SIZES.xs} style={{ color: isActive && sortDir === "asc" ? palette.accent1 : palette.text3, opacity: isActive && sortDir === "asc" ? 1 : 0.3 }} />
        <ChevronDown size={ICON_SIZES.xs} style={{ color: isActive && sortDir === "desc" ? palette.accent1 : palette.text3, opacity: isActive && sortDir === "desc" ? 1 : 0.3, marginTop: -3 }} />
      </span>
    );
  };

  return (
    <Card style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>
      <div style={{ overflowX: "auto", flex: 1, display: "flex", flexDirection: "column" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${palette.cardBorder}` }}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  style={{
                    padding: "10px 14px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: sortCol === col.key ? palette.accent1 : palette.text2,
                    fontSize: FONT_SIZES.xs,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    background: `${palette.accent1}06`,
                    cursor: col.sortable === false ? "default" : "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {col.label}
                  <SortIcon col={col} />
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY }}>
            <tbody>
              {sorted.map((member) => (
                <tr
                  key={member.id || member.afpId}
                  onClick={() => navigate("memberDetail", member)}
                  style={{ borderBottom: `1px solid ${palette.cardBorder}`, cursor: "pointer", transition: "background 0.15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = `${palette.accent1}06`)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "10px 14px", fontWeight: 500, color: palette.text1 }}>
                    {getDisplayName(member)}
                  </td>
                  <td style={{ padding: "10px 14px", color: palette.text2 }}>
                    {getOrgDisplay(member) || "—"}
                  </td>
                  <td style={{ padding: "10px 14px", color: palette.text2 }}>{member.email || "—"}</td>
                  <td style={{ padding: "10px 14px", color: palette.text3, fontSize: FONT_SIZES.xs }}>
                    {getTypeLabel(member) || "—"}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <PathwayBadge stage={member.pathway} effective={effectiveMap[member.id]} small />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {member.isDonor && <Badge label="D" color={palette.tagDonor} small />}
                      {member.isBoardMember && <Badge label="B" color={palette.tagBoard} small />}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", color: palette.text2 }}>{member.assignedDirector || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};
